import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { analytics } from '@helpers/analytics'
import { productListViewed } from '@helpers/products'
import CollectionDetails from '@components/collection/CollectionDetails'
import Hero from './Hero'
import CollectionWrapper from './Collection/index'
import CollectionContext from './collectionContext'
import styles from './styles.module.scss'
import type {
  Collection,
  CollectionConfig,
  CollectionListMeta
} from 'types/collection'
import CollectionFilter from './CollectionFilter'
import { FilterBuild } from './CollectionFilter/FiltersSidePanel/types'
import { FilterOption } from 'types/filters'
import {
  resetDynamicFilters,
  selectDynamicFilter
} from '@store/redux/actions/filterActions'
import useLocalStorage from '@helpers/hooks/useLocalStorage'
import { useFetchVariants } from './hooks/useFetchVariants'
import { useFilterContext } from './hooks/useFilterContext'
import FilterToggleButton from './CollectionFilter/FiltersSidePanel/FilterToggleButton'
import { getCustomerFitCondensed } from '@store/redux/selectors/customerFitSelectors'
import { Fit } from 'types/fit'
import { BUILD } from '@consts/index'
import getCollectionProductData from '@helpers/filters/getCollectionProductData'
import CustomHead from '@components/Head'
import { CollectionProductFamilyFilter } from './CollectionProductFamilyFilter'
import {
  filterByProductFeed,
  getAvailableProductStyles,
  getEcomProductCategoriesByType
} from './helpers'
import { createFilterStorage, getFiltersOptionsState } from '@helpers/filters'
import { initFit } from '@helpers/initFit'
import { setStoredValue } from '@helpers/hooks/useStorage'

const ProductsPromo = dynamic(() => import('@components/ui/ProductsPromo'))

const MAX_NUMBER_OF_ECOM_CATEGORY_HEADER = 2 // 1 + 'All' option

function getLimitForEcomHeaderCategories(collectionHandle: string) {
  if (
    collectionHandle &&
    EXCLUDED_ECOM_HEADERS.includes(collectionHandle.toLowerCase().trim())
  ) {
    return MAX_NUMBER_OF_ECOM_CATEGORY_HEADER + 1
  }

  return MAX_NUMBER_OF_ECOM_CATEGORY_HEADER
}

const EXCLUDED_ECOM_HEADERS = ['denim']

function getEcomProductFilters(
  collections: Collection[],
  collectionHandle: string
) {
  const ecomProductsByHeader = getEcomProductCategoriesByType(
    collections,
    'ecom_product_header'
  )
  const ecomProductsByGrouping = getEcomProductCategoriesByType(
    collections,
    'ecom_product_grouping'
  )
  const maxLimitEcomHeaderCategories =
    getLimitForEcomHeaderCategories(collectionHandle)

  if (ecomProductsByHeader.length > maxLimitEcomHeaderCategories) {
    return {
      values: ecomProductsByHeader,
      type: 'header'
    }
  }

  if (
    ecomProductsByHeader.length === maxLimitEcomHeaderCategories &&
    ecomProductsByGrouping.length > 2 &&
    !EXCLUDED_ECOM_HEADERS.includes(collectionHandle)
  ) {
    return {
      values: ecomProductsByGrouping,
      type: 'group'
    }
  }

  return {
    values: getAvailableProductStyles(collections),
    type: 'style'
  }
}

function getCollectionProductsCount({
  collections,
  filterOptions,
  variantExist
}: {
  collections: Collection[]
  filterOptions: FilterOption[]
  variantExist: boolean
}) {
  const { collectionsWithFilteredProducts } = getCollectionProductData({
    collections,
    filterOptions,
    variantExist
  })
  return collectionsWithFilteredProducts.reduce((total, collection) => {
    return total + collection.items.length
  }, 0)
}

type CollectionListProps = {
  collectionHandle: Collection['handle']
  collectionData: {
    title: string
    byline: string
    description: string
    collections: Collection[]
    collectionConfig?: CollectionConfig
    meta: CollectionListMeta
  }
  filterBuild: FilterBuild
}

function CollectionList({
  collectionHandle,
  collectionData,
  filterBuild
}: CollectionListProps) {
  const router = useRouter()
  const dispatch = useDispatch()
  const { title, byline, collections, meta, description } = collectionData
  const { collectonHandle: queryCollectionHandle, style: queryProductStyle } =
    router.query as {
      category: string
      collectonHandle: string
      style: string
    }

  const [selectedProductStyle, setSelectedProductStyle] =
    useState<string>('All')

  const { fit }: { fit: Partial<Fit> } = useSelector(getCustomerFitCondensed)

  const { updatedCollections, variantExist } = useFetchVariants(
    collectionHandle,
    collections
  )

  const [collection, setCollection] = useState<Collection[]>(collections)

  const { isFiltersOpen, closeFilters, openFilters, showFilters } =
    useFilterContext({
      metaFilters: meta.filters
    })

  const browsingFiltersStorage = initFit()
  const filterOptions = getFiltersOptionsState(browsingFiltersStorage || {})

  const { values: ecomProductFilters, type: ecomProductFiltersType } =
    getEcomProductFilters(updatedCollections, collectionHandle)

  const isCollectionEcomCategoriesFilterVisible = ecomProductFilters.length > 2

  const totalResults = getCollectionProductsCount({
    collections: collection,
    filterOptions,
    variantExist
  })

  const [isSavedFitApplied, setIsSavedFitApplied] = useLocalStorage(
    'plp_filters_saved_fit_applied',
    false
  )

  const [isApplyFitActive, setIsApplyFitActive] =
    useState<boolean>(isSavedFitApplied)

  useEffect(() => {
    const productStyle = ecomProductFilters.includes(queryProductStyle)
      ? queryProductStyle
      : 'All'

    setSelectedProductStyle(productStyle)
  }, [queryProductStyle])

  const filteredCollections = useMemo(() => {
    let collectionsData = updatedCollections

    if (selectedProductStyle !== 'All') {
      collectionsData = filterByProductFeed(
        updatedCollections,
        selectedProductStyle,
        ecomProductFiltersType
      )
    }

    return collectionsData
  }, [selectedProductStyle, updatedCollections, ecomProductFiltersType])

  useEffect(() => {
    setCollection(filteredCollections)
  }, [filteredCollections])

  const handleProductFamilyClick = (filter: string) => {
    if (filter === 'All') {
      delete router.query.style
    } else {
      router.query.style = filter
    }

    router.push(router, undefined, { shallow: true })
  }

  useEffect(() => {
    if (variantExist) {
      const eventProducts = collection.flatMap(collectionEntry => {
        return collectionEntry.items.map((product, index) => ({
          quantity: 1,
          name: collectionEntry.title,
          color: product.title,
          price: parseFloat(product.price),
          position: index + 1,
          url: product.handle,
          image_url: product.images[0]?.src,
          brand: 'SPOKE',
          product_id: product.product_feed?.product_id
        }))
      })
      productListViewed({
        context: 'Collection',
        listId: collectionHandle,
        listName: title,
        products: eventProducts
      })
    }
  }, [variantExist, collection, collectionHandle, title])

  const collectionContext = useMemo(
    () => ({
      collectionHandle,
      hasMultipleProductTypes: collection?.length > 1,
      collectionMainTitle: title,
      closeFilters,
      openFilters,
      isFiltersOpen,
      isApplyFitActive,
      setIsApplyFitActive
    }),
    [isFiltersOpen, isApplyFitActive]
  )

  const handleFilterClear = () => {
    setIsApplyFitActive(false)
    setIsSavedFitApplied(false)
    dispatch(resetDynamicFilters())
  }

  const handleFilterApply = useCallback(
    (options: FilterOption[]) => {
      const isSavedFitValid = filterOptions.every(
        option =>
          fit[option.type as keyof Fit] ===
          String(option.value.replace(BUILD, '').trim())
      )
      setIsSavedFitApplied(isSavedFitValid && isSavedFitApplied)
      dispatch(resetDynamicFilters())
      dispatch(selectDynamicFilter(options))
      const newFilters = createFilterStorage(options)
      setStoredValue('sessionStorage', 'filters', newFilters)
      closeFilters()
    },
    [filterOptions, closeFilters, dispatch, fit, setIsSavedFitApplied]
  )

  const handleApplySavedFitClick = (state: boolean) => {
    setIsSavedFitApplied(state)
    setIsApplyFitActive(state)
  }
  return (
    <>
      <CustomHead
        includeNoFollow={meta.page_crawl}
        title={meta.seo_title}
        desc={meta.seo_description}
        image={meta.seo_image}
      />
      <div style={{ minHeight: 800 }}>
        <CollectionContext.Provider value={collectionContext}>
          <section className={styles.CollectionHeader}>
            <Hero
              title={
                selectedProductStyle === 'All' ? title : selectedProductStyle
              }
              parentTitle={title}
              parentTitleVisible={isCollectionEcomCategoriesFilterVisible}
              byline={byline}
            />

            {isCollectionEcomCategoriesFilterVisible && (
              <CollectionProductFamilyFilter
                filters={ecomProductFilters}
                selectedFilter={selectedProductStyle}
                selectProductFamilyFilter={handleProductFamilyClick}
              />
            )}
          </section>

          {showFilters && (
            <FilterToggleButton
              totalResults={totalResults}
              filterBuild={filterBuild}
              isApplyFitActive={isApplyFitActive}
            />
          )}

          <section className={styles.collectionContainer}>
            <div className={styles.collectionGridContainer}>
              {showFilters && (
                <div className={styles.collectionFilterWrapper}>
                  <CollectionFilter
                    filterOptions={filterOptions}
                    filterBuild={filterBuild}
                    onFilterClear={handleFilterClear}
                    onFilterApply={handleFilterApply}
                    onApplyFitToggle={handleApplySavedFitClick}
                  />
                </div>
              )}
              <div className={styles.collectionWrapper}>
                <CollectionWrapper
                  collections={collection}
                  filterOptions={filterOptions}
                  variantExist={variantExist}
                  onFilterClear={handleFilterClear}
                />
              </div>
            </div>
          </section>

          <div className={styles.collection}>
            {description && <CollectionDetails copy={description} />}
            <ProductsPromo
              pageHandle={queryCollectionHandle}
              onLinkClick={productsPromotedCollection => {
                analytics('Click', {
                  category: 'Collection Page',
                  label: 'Collection Promo',
                  collection_promoted: productsPromotedCollection
                })
              }}
            />
          </div>
        </CollectionContext.Provider>
      </div>
    </>
  )
}

export default CollectionList
